import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { InputField } from "@/components/input-field/InputField";
import { SelectComponent } from "@/components/select/Select";
import { AppRoutes } from "@/constants/routes";
import {
  getRequestPolicy,
  getTiposSolicitud,
} from "@/services/request-policies.service";
import { AppColors } from "@/styles/colors";
import { isEmpty } from "@/utils/query-string";
import { Card, Flex, Grid, GridItem } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  ClasePoliticaEnum,
  RequestPolicyDetail,
  requestPolicySchema,
} from "../schema/request-policies.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  handleUpdatePolicyHandler,
  resetUpdateFormValor,
} from "../schema/handler-functions";
import { PolicyAmountInputView } from "./views/PolicyAmountInputView";
import { PolicyTimeAndDayValueInputView } from "./views/PolicyTimeAndDayInputView";
import { SwitchInput } from "@/components/switch/switch-component";
import { useRoles } from "@/hooks/useRoles";

export const RequestPolicyUpdatePage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  const { id } = useParams();

  useEffect(() => {
    setIsActive(true);
  }, []);

  const {
    control,
    formState: { errors },
    getValues,
    setValue,
  } = useForm({
    resolver: zodResolver(requestPolicySchema),
  });

  const navigate = useNavigate();

  const [loadingPolicy, setLoadingPolicy] = useState<boolean>(false);

  const [policy, setPolicy] = useState<RequestPolicyDetail | null>();

  const fetchPolicy = async () => {
    setLoadingPolicy(true);

    const response = await getRequestPolicy({ id: Number(id) });

    setPolicy(response);
    setLoadingPolicy(false);
  };

  useEffect(() => {
    if (isActive && id) fetchPolicy();
  }, [isActive, id]);

  useEffect(() => {
    if (policy) {
      setValue("activo", policy?.activo);
      setValue("nombre", policy?.nombrePolitica);
      setValue("tipoSolicitud", policy?.idTipoSolicitud);
      setValue("clase", [policy?.clase]);

      if (policy?.clase === ClasePoliticaEnum.TopeDiario) {
        setValue("monto", policy?.valor);
      }

      if (policy?.clase === ClasePoliticaEnum.RangoSemanal) {
        setValue("dias", policy?.valor?.split(", "));
      }

      if (policy?.clase === ClasePoliticaEnum.Horario) {
        const horarios = policy?.valor?.split("-");
        setValue("horaDesde", horarios?.[0]);
        setValue("horaHasta", horarios?.[1]);
      }
    }
  }, [policy]);

  const [tiposSolicitud, setTiposSolicitud] = useState<
    { id: string; descripcion: string }[]
  >([]);

  const [loadingTiposSolicitud, setLoadingTiposSolicitud] =
    useState<boolean>(false);
  const [loadingEditPolicy, setLoadingEditPolicy] = useState<boolean>(false);
  const fetchTiposSolicitud = async () => {
    setLoadingTiposSolicitud(true);

    const response = await getTiposSolicitud();

    setTiposSolicitud(response);

    setLoadingTiposSolicitud(false);
  };

  useEffect(() => {
    if (isActive) fetchTiposSolicitud();
  }, [isActive]);

  const handleCancel = () => {
    navigate(AppRoutes.REQUEST_POLICIES);
  };

  const {fetchPermissions} = useRoles()

  const handleEditPolicy = async () => {
    handleUpdatePolicyHandler({
      getValues,
      id: Number(id),
      navigate,
      setLoadingUpdatePolicy: setLoadingEditPolicy,
      fetchPermissions
    });
  };

  const watchedValues = useWatch({ control });

  const hasNoFilledValues = useMemo(() => {
    const { clase, dias, horaDesde, horaHasta, monto, nombre, tipoSolicitud } =
      watchedValues || {};

    return (
      isEmpty(tipoSolicitud) ||
      isEmpty(nombre) ||
      isEmpty(clase) ||
      (isEmpty(monto) && clase?.[0] === ClasePoliticaEnum.TopeDiario) ||
      (isEmpty(dias) && clase?.[0] === ClasePoliticaEnum.RangoSemanal) ||
      (isEmpty(horaDesde) && clase?.[0] === ClasePoliticaEnum.Horario) ||
      (isEmpty(horaHasta) && clase?.[0] === ClasePoliticaEnum.Horario)
    );
  }, [watchedValues]);

  const [clase] = useWatch({
    control,
    name: ["clase"],
  });

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Editar política de negocio"
        addButtonLabel="Confirmar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        handleAdd={handleEditPolicy}
        disableAdd={
          loadingTiposSolicitud ||
          hasNoFilledValues ||
          loadingPolicy ||
          loadingEditPolicy
        }
        loading={loadingEditPolicy || loadingPolicy}
      />
      <Card.Root maxW="full" marginTop="30px" borderRadius="8px">
        <Card.Body
          paddingX="48px"
          paddingY="30px"
          borderRadius="8px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        >
          <Grid templateColumns="repeat(4, 1fr)">
            <GridItem paddingX="16px" paddingY="8px">
              <SelectComponent
                minHeight="40px"
                labelText="Aplica a"
                control={control}
                name="tipoSolicitud"
                multiple
                placeholder="Buscar..."
                items={tiposSolicitud.map((tipo) => ({
                  label: tipo?.descripcion,
                  value: tipo?.id,
                }))}
                required
                disabled={
                  loadingPolicy || loadingEditPolicy || loadingTiposSolicitud
                }
              />
            </GridItem>
            <GridItem paddingX="16px" paddingY="8px">
              <InputField
                control={control}
                inputName="nombre"
                labelText="Nombre"
                placeholder="Ingresar nombre..."
                required
                disabled={loadingPolicy || loadingEditPolicy}
              />
            </GridItem>

            <GridItem paddingX="16px" paddingY="8px">
              <SelectComponent
                minHeight="40px"
                items={[
                  {
                    label: ClasePoliticaEnum.TopeDiario,
                    value: ClasePoliticaEnum.TopeDiario,
                  },
                  {
                    label: ClasePoliticaEnum.RangoSemanal,
                    value: ClasePoliticaEnum.RangoSemanal,
                  },
                  {
                    label: ClasePoliticaEnum.Horario,
                    value: ClasePoliticaEnum.Horario,
                  },
                ]}
                control={control}
                name="clase"
                labelText="Clase"
                clearable
                closeOnSelect
                error={errors?.clase}
                required
                disabled={loadingPolicy || loadingEditPolicy}
                handleChange={() => resetUpdateFormValor({ setValue, policy })}
              />
            </GridItem>
            <PolicyAmountInputView
              clase={clase}
              control={control}
              errors={errors}
              loading={loadingEditPolicy}
            />
          </Grid>
          <Flex gap={"50px"}></Flex>
        </Card.Body>
      </Card.Root>
      <SwitchInput control={control} name="activo" label="Activada" />
      <PolicyTimeAndDayValueInputView
        control={control}
        clase={clase}
        loading={loadingEditPolicy}
      />
    </Flex>
  );
};
